import { t } from "@lingui/macro";
import {
  ApiSmpGpt,
  FailureApi,
  failureApiGenerator,
  IApiSmpGptAskRequest,
  IFailureApiPayload,
} from "@smp/api";
import { useSnackbar } from "notistack";
import { useCallback, useRef, useState } from "react";
import { useMutation, UseMutationOptions } from "react-query";
import { useUnmount } from "react-use";

import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { gscSendUserParams } from "@/utils";
import { yaReachGoal } from "@/utils/ya_reach_goal";

export interface IGptChunk {
  response: string[];
  cost: number;
  raw: {
    choices: Array<{
      finish_reason: string;
      index: number;
      message: {
        content: string;
        role: string;
      };
    }>;
    created: number;
    id: string;
    model: string;
    object: string;
    usage: {
      completion_tokens: number;
      prompt_tokens: number;
      total_tokens: number;
    };
  };
}

export const useMutationGptAskAsk = (
  queryOptions?: UseMutationOptions<string, unknown, IApiSmpGptAskRequest>,
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { failureTranslationGet } = useFailureTranslator();
  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<
    ReadableStreamDefaultReader<Uint8Array> | undefined
  >();
  const [data, setData] = useState<string | undefined>();

  const mutation = useMutation<string, unknown, IApiSmpGptAskRequest>(
    (params) => {
      if (abortControllerRef.current) {
        abortControllerRef.current?.abort();
      }

      abortControllerRef.current = new AbortController();

      params.prompt = params.prompt.replace(/\.$/, "");

      if (/table|таблиц/gim.test(params.prompt)) {
        params.prompt += ". " + t`Use Markdown for markup.`;
      }

      return ApiSmpGpt.Ask.ask(params, abortControllerRef.current?.signal)
        .then(async (response) => {
          const contentType = response.headers.get("content-type");

          let text = "";

          if (contentType?.includes("text/html")) {
            text = await response.text();
          } else {
            readerRef.current = response.body?.getReader();

            if (readerRef.current) {
              try {
                const decoder = new TextDecoder("utf-8");

                while (true) {
                  const { value, done } = await readerRef.current.read();

                  if (done) {
                    setData(undefined);
                    break;
                  } else {
                    const valueDecoded = decoder.decode(value);
                    try {
                      const jsonChunks = valueDecoded.split("\n");

                      for (let jsonChunk of jsonChunks) {
                        if (jsonChunk) {
                          const chunk: IGptChunk = JSON.parse(jsonChunk);

                          text += chunk.response.join("");

                          setData(text);
                        }
                      }
                    } catch (e) {}
                  }
                }
              } catch (e) {
                text = "";
                setData(undefined);
              }
            }

            if (abortControllerRef.current?.signal.aborted) {
              setData(undefined);
              return "";
            }
          }

          if (!text) {
            throw failureApiGenerator(
              {
                body: {
                  error_type: "FETCH_INTERNAL",
                } as IFailureApiPayload["body"],
                response: new Response(),
              },
              {},
            );
          }

          if (params.tag === "advisor") {
            gscSendUserParams({ advisor_open: true });
            yaReachGoal("advisor_open");
          }

          return text;
        })
        .catch((error) => {
          if (error instanceof FailureApi) {
            if (error.code !== "API__ROOT__ABORT_ERROR") {
              throw error;
            }
          }

          return "";
        });
    },
    {
      onError: (e) => {
        enqueueSnackbar(failureTranslationGet(e), {
          variant: "error",
        });
      },
      ...queryOptions,
    },
  );

  const abort = useCallback(() => {
    setData(undefined);

    if (readerRef.current) {
      readerRef.current.cancel("abort");
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  useUnmount(() => {
    abort();
  });

  return {
    ...mutation,
    abort,
    data,
  };
};
